const CSV_SEPARATOR = ';';
const CSV_MIME_TYPE = 'text/csv;charset=utf-8';

const toList = (value) => (Array.isArray(value) ? value : []);

const normalizeCell = (value) => {
  const normalized = String(value ?? '')
    .replace(/\u00a0/g, ' ')
    .replace(/\r\n?/g, '\n');
  const trimmedStart = normalized.trimStart();
  const couldBeFormula = /^[=+@]/.test(trimmedStart)
    || (/^-/.test(trimmedStart) && trimmedStart !== '-' && !/^-\d+(?:[.,]\d+)?$/.test(trimmedStart));

  return couldBeFormula ? `'${normalized}` : normalized;
};

const escapeCell = (value) => `"${normalizeCell(value).replace(/"/g, '""')}"`;
const serializeRow = (cells) => toList(cells).map(escapeCell).join(CSV_SEPARATOR);

const getCsvFileName = (fileName) => {
  const normalized = String(fileName || 'crm').trim() || 'crm';
  if (/\.pdf$/i.test(normalized)) return normalized.replace(/\.pdf$/i, '.csv');
  if (/\.csv$/i.test(normalized)) return normalized;
  return `${normalized}.csv`;
};

export function buildCrmCsvContent(report = {}) {
  const rows = [];
  const addRow = (...cells) => rows.push(cells);
  const addBlankRow = () => rows.push([]);

  addRow(report.title || 'Relatório do CRM');
  if (report.subtitle) addRow(report.subtitle);

  const metadata = toList(report.metadata);
  if (metadata.length) {
    addBlankRow();
    addRow('Informação', 'Valor');
    metadata.forEach(([label, value]) => addRow(label, value));
  }

  const highlights = toList(report.highlights);
  if (highlights.length) {
    addBlankRow();
    addRow('Indicador', 'Valor');
    highlights.forEach(([label, value]) => addRow(label, value));
  }

  const keyValues = toList(report.keyValues);
  if (keyValues.length) {
    addBlankRow();
    addRow('Dados principais', 'Valor');
    keyValues.forEach(([label, value]) => addRow(label, value));
  }

  toList(report.sections).forEach((section) => {
    addBlankRow();
    addRow(section.title || 'Dados');

    const columns = toList(section.columns);
    const sectionRows = toList(section.rows);
    if (columns.length) {
      addRow(...columns.map((column) => column.label));
      if (sectionRows.length) {
        sectionRows.forEach((row) => {
          addRow(...columns.map((column) => row?.[column.key]));
        });
      } else if (section.emptyLabel) {
        addRow(section.emptyLabel);
      }
    }

    const paragraphs = toList(section.paragraphs);
    if (paragraphs.length) {
      addRow('Descrição');
      paragraphs.forEach((paragraph) => addRow(paragraph));
    }
  });

  return `\uFEFF${rows.map(serializeRow).join('\r\n')}\r\n`;
}

export function downloadCrmCsv(report = {}, options = {}) {
  const content = buildCrmCsvContent(report);
  const fileName = getCsvFileName(report.fileName);
  const BlobConstructor = options.BlobConstructor || window.Blob;
  const urlApi = options.urlApi || window.URL;
  const documentRef = options.documentRef || document;
  const blob = new BlobConstructor([content], { type: CSV_MIME_TYPE });
  const objectUrl = urlApi.createObjectURL(blob);
  const anchor = documentRef.createElement('a');

  anchor.href = objectUrl;
  anchor.download = fileName;
  anchor.style.display = 'none';
  documentRef.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  urlApi.revokeObjectURL(objectUrl);

  return fileName;
}

export const crmCsvInternals = {
  escapeCell,
  getCsvFileName,
};
